/**
 * Volet de synthèse — Fetch de la synthèse riche d'une fiche épinglée.
 *
 * Calqué sur `useSearchPreview` : TanStack Query, cache 60 s, `entityId` FIGÉ au
 * pin (cross-entité correct). Endpoint dédié `GET /api/search/summary` (séparé du
 * preview ⌘K pour ne pas dégrader le SLO de la recherche). Expose `refetch` pour
 * le bouton « Réessayer » de l'état d'erreur.
 */

import { useQuery } from '@tanstack/react-query'
import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { queryKeys } from '@/lib/queryKeys'
import type { PinnedSummary, SummaryDetail } from '@/components/summary/types'

export function useSummaryDetail(pinned: PinnedSummary | null) {
    return useQuery<SummaryDetail>({
        queryKey: queryKeys.search.summary(pinned?.type ?? '', pinned?.id ?? ''),
        queryFn: async ({ signal }) => {
            const params = new URLSearchParams({
                type: pinned!.type,
                id: pinned!.id,
                entity_id: pinned!.entityId ?? '',
            })
            const res = await apiFetch(`${API_BASE}/search/summary?${params.toString()}`, {
                headers: pinned!.entityId ? { 'X-Entity-Id': pinned!.entityId } : undefined,
                signal,
            })
            if (!res.ok) await handleApiError(res)
            return res.json()
        },
        enabled: !!pinned,
        staleTime: 60_000,
        // Une tentative de reprise (transitoire) ; l'erreur dure expose « Réessayer ».
        retry: 1,
    })
}
